import React, { useState, useEffect } from 'react';
import Select from './Select';
import Input from './Input';
import Button from './Button';
import Icon from '../AppIcon';

const GlobalFilterBar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: 'last-30-days',
    company: 'all',
    region: 'all',
    customStartDate: '',
    customEndDate: ''
  });

  // Load saved filters from sessionStorage
  useEffect(() => {
    const savedFilters = sessionStorage.getItem('globalFilters');
    if (savedFilters) {
      setFilters(JSON.parse(savedFilters));
    }
  }, []);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem('globalFilters', JSON.stringify(filters));
  }, [filters]);

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'this-month', label: 'This Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'this-quarter', label: 'This Quarter' },
    { value: 'this-year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const companyOptions = [
    { value: 'all', label: 'All Companies' },
    { value: 'acme-corp', label: 'Acme Corporation' },
    { value: 'tech-solutions', label: 'Tech Solutions Inc.' },
    { value: 'global-enterprises', label: 'Global Enterprises' },
    { value: 'innovation-labs', label: 'Innovation Labs' },
    { value: 'future-systems', label: 'Future Systems' }
  ];

  const regionOptions = [
    { value: 'all', label: 'All Regions' },
    { value: 'north-america', label: 'North America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia-pacific', label: 'Asia Pacific' },
    { value: 'latin-america', label: 'Latin America' },
    { value: 'middle-east-africa', label: 'Middle East & Africa' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    // Emit filter change event for other components to listen
    window.dispatchEvent(new CustomEvent('filtersChanged', { detail: filters }));
    setIsExpanded(false);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      dateRange: 'last-30-days',
      company: 'all',
      region: 'all',
      customStartDate: '',
      customEndDate: ''
    };
    setFilters(defaultFilters);
    window.dispatchEvent(new CustomEvent('filtersChanged', { detail: defaultFilters }));
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.dateRange !== 'last-30-days') count++;
    if (filters.company !== 'all') count++;
    if (filters.region !== 'all') count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <div className="sticky top-16 z-[999] bg-card border-b border-border">
      <div className="px-4 lg:px-6 py-3">
        {/* Filter Bar Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </div>

            {/* Quick Filter Pills - Desktop */}
            <div className="hidden lg:flex items-center space-x-2">
              <Select
                options={dateRangeOptions}
                value={filters.dateRange}
                onChange={(value) => handleFilterChange('dateRange', value)}
                className="w-40"
              />
              <Select
                options={companyOptions}
                value={filters.company}
                onChange={(value) => handleFilterChange('company', value)}
                className="w-48"
              />
              <Select
                options={regionOptions}
                value={filters.region}
                onChange={(value) => handleFilterChange('region', value)}
                className="w-40"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleResetFilters}>
                <Icon name="RotateCcw" size={14} />
                Reset
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="lg:hidden"
            >
              <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={14} />
              {isExpanded ? 'Less' : 'More'}
            </Button>
          </div>
        </div>

        {/* Expanded Mobile Filters */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border lg:hidden">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Date Range"
                options={dateRangeOptions}
                value={filters.dateRange}
                onChange={(value) => handleFilterChange('dateRange', value)}
              />
              <Select
                label="Company"
                options={companyOptions}
                value={filters.company}
                onChange={(value) => handleFilterChange('company', value)}
              />
              <Select
                label="Region"
                options={regionOptions}
                value={filters.region}
                onChange={(value) => handleFilterChange('region', value)}
              />
            </div>

            {/* Custom Date Range */}
            {filters.dateRange === 'custom' && (
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Start Date"
                  type="date"
                  value={filters.customStartDate}
                  onChange={(e) => handleFilterChange('customStartDate', e.target.value)}
                />
                <Input
                  label="End Date"
                  type="date"
                  value={filters.customEndDate}
                  onChange={(e) => handleFilterChange('customEndDate', e.target.value)}
                />
              </div>
            )}

            <div className="mt-4 flex items-center justify-end space-x-2">
              <Button variant="outline" size="sm" onClick={handleResetFilters}>
                Reset
              </Button>
              <Button size="sm" onClick={handleApplyFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Custom Date Range - Desktop */}
        {filters.dateRange === 'custom' && (
          <div className="hidden lg:flex items-center space-x-4 mt-3 pt-3 border-t border-border">
            <Input
              label="Start Date"
              type="date"
              value={filters.customStartDate}
              onChange={(e) => handleFilterChange('customStartDate', e.target.value)}
              className="w-40"
            />
            <Input
              label="End Date"
              type="date"
              value={filters.customEndDate}
              onChange={(e) => handleFilterChange('customEndDate', e.target.value)}
              className="w-40"
            />
            <Button size="sm" onClick={handleApplyFilters}>
              Apply
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalFilterBar;